import React from 'react';
import { 
  XMarkIcon,
  CheckIcon,
  CreditCardIcon,
  UsersIcon,
  GlobeAltIcon,
  LinkIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { Plan } from '../types/plan.types';

interface PlanPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  plan: Plan | null;
}

const PlanPreview: React.FC<PlanPreviewProps> = ({ isOpen, onClose, plan }) => {
  if (!isOpen || !plan) {return null;}

  const formatPrice = (price: number) => {
    return price === 0 ? 'Free' : `₩${price}`;
  };

  const formatFeatureValue = (value: number | boolean) => {
    if (typeof value === 'boolean') {
      return value ? 'Included' : 'Not Included';
    }
    if (value === -1) {return 'Unlimited';}
    if (value === 0) {return 'Not Included';}
    return value.toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {plan.color && (
                <div 
                  className="w-6 h-6 rounded-full"
                  style={{ backgroundColor: plan.color }}
                />
              )}
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {plan.displayName}
                  {plan.badge && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                      {plan.badge}
                    </span>
                  )}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Plan Details Preview</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Plan Overview */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-4 mb-4">
              {plan.color && (
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${plan.color}20` }}
                >
                  <CreditCardIcon 
                    className="h-6 w-6"
                    style={{ color: plan.color }}
                  />
                </div>
              )}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {plan.displayName}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {plan.description}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                  {formatPrice(plan.price.monthly)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">per month</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                  {formatPrice(plan.price.yearly)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">per year</div>
                {plan.price.yearlyDiscount && (
                  <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                    Save {plan.price.yearlyDiscount}%
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Core Features */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <ChartBarIcon className="h-5 w-5 mr-2" />
              Core Features
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <LinkIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Links per Month
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatFeatureValue(plan.features.linksPerMonth)}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <GlobeAltIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Custom Domains
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatFeatureValue(plan.features.customDomains)}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <UsersIcon className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Team Members
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatFeatureValue(plan.features.teamMembers)}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ChartBarIcon className="h-5 w-5 text-orange-600 dark:text-orange-400 mr-2" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Clicks per Month
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatFeatureValue(plan.features.clicksPerMonth)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Advanced Features */}
          {(plan.features.whiteLabel || 
            plan.features.apiAccess || 
            plan.features.prioritySupport || 
            plan.features.analytics) && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <CogIcon className="h-5 w-5 mr-2" />
                Advanced Features
              </h4>
              <div className="space-y-3">
                {plan.features.whiteLabel && (
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      White-label & Custom Branding
                    </span>
                  </div>
                )}
                {plan.features.apiAccess && (
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      API Access & Integrations
                    </span>
                  </div>
                )}
                {plan.features.prioritySupport && (
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Priority Customer Support
                    </span>
                  </div>
                )}
                {plan.features.analytics && (
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Advanced Analytics & Reporting
                    </span>
                  </div>
                )}
                {plan.features.advancedSecurity && (
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Advanced Security Features
                    </span>
                  </div>
                )}
                {plan.features.webhooks && (
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Webhooks & Real-time Events
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Plan Status */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ShieldCheckIcon className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Plan Status
                </span>
              </div>
              <div className="flex items-center">
                <div 
                  className={`w-2 h-2 rounded-full mr-2 ${
                    plan.isActive ? 'bg-green-500' : 'bg-red-500'
                  }`}
                />
                <span className={`text-sm font-medium ${
                  plan.isActive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {plan.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            
            {plan.createdAt && (
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Created
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {new Date(plan.createdAt).toLocaleDateString()}
                </span>
              </div>
            )}
            
            {plan.updatedAt && (
              <div className="flex items-center justify-between mt-1">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Last Updated
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {new Date(plan.updatedAt).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 px-6 py-4 rounded-b-2xl">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanPreview;